import { useEffect, useState } from 'react'

import { useForm, useStore } from '@tanstack/react-form'
import { CreateApplicationRequest } from 'commons/types/DTO/bank/applications/manage/CreateApplicationRequest'
import { Keys, useStateManager } from 'state-manager'
import { CompanySelect, FF, Toggle } from 'ui/components'
import { DialogContent, DialogFooter, DialogHeader, useDialog } from 'ui/components/DialogV2'
import { Validators } from 'ui/components/Form/validators'
import { EmailInput, TextInput, TextareaInput, USPhoneInput } from 'ui/components/InputV2'
import { Toast } from 'ui/components/Toast'
import { Col, Col2x, FormRow } from 'ui/themes/globalStyles'

import { useCreateApplication } from '../../../../hooks/useCreateApplication'
import CreateApplicationDialogProps from './CreateApplicationDialogProps'

export const CreateApplication = ({ showExistingRelationshipToggle }: CreateApplicationDialogProps) => {
  const dialog = useDialog()
  const sm = useStateManager()
  const [chooseExistingRelationship, setChooseExistingRelationship] = useState(false)

  const { routine: createApplication } = useCreateApplication({
    onSuccess: async () => {
      dialog.anywayClose()
      Toast.success('Application has been saved')
      await sm.invalidate(Keys.BankApplication.List)
    },
    onError: (error) => {
      Toast.errorSmall(error.message)
    }
  })

  const form = useForm({
    defaultValues: new CreateApplicationRequest(),
    onSubmit: async ({ value }) => createApplication(value)
  })

  const isDirty = useStore(form.store, (s) => s.isDirty)
  const isSubmitSuccessful = useStore(form.store, (s) => s.isSubmitSuccessful)

  const handleChooseExistingRelationship = () => {
    if (chooseExistingRelationship) {
      setChooseExistingRelationship(false)
      form.deleteField('companyId')
    } else {
      setChooseExistingRelationship(true)
      form.deleteField('companyName')
    }
  }

  useEffect(() => {
    dialog.doNotClose(isDirty && !isSubmitSuccessful)
  }, [dialog, isSubmitSuccessful, isDirty])

  return (
    <>
      <DialogHeader title="New Application" />
      <DialogContent>
        {showExistingRelationshipToggle && (
          <FormRow>
            <Col>
              <Toggle value={chooseExistingRelationship} onChange={() => handleChooseExistingRelationship()} /> Choose
              existing relationship
            </Col>
          </FormRow>
        )}
        <FormRow>
          <Col>
            {chooseExistingRelationship && (
              <form.Field
                name="companyId"
                validators={{
                  onBlur: ({ value }) => Validators.required.string(value)
                }}
                children={(companyIdField) => (
                  <FF
                    required
                    label="Existing relationship"
                    field={companyIdField}
                    input={(inputProps) => (
                      <CompanySelect
                        data-qa="existingRelationship"
                        hideMyCompaniesFilter={true}
                        hint='For companies in the "Customer" status'
                        {...inputProps}
                      />
                    )}
                  />
                )}
              />
            )}
            {!chooseExistingRelationship && (
              <form.Field
                name="companyName"
                validators={{
                  onBlur: ({ value }) => Validators.required.string(value)
                }}
                children={(companyNameField) => (
                  <FF
                    label="Company Name"
                    field={companyNameField}
                    required
                    input={(inputProps) => <TextInput autoComplete="off" data-qa="Company Name" {...inputProps} />}
                  />
                )}
              />
            )}
          </Col>
        </FormRow>
        <FormRow>
          <Col>
            <form.Field
              name="contactFirstName"
              children={(contactFirstNameField) => (
                <FF
                  label="First Name"
                  field={contactFirstNameField}
                  input={(inputProps) => <TextInput data-qa="First Name" {...inputProps} />}
                />
              )}
            />
          </Col>
          <Col>
            <form.Field
              name="contactLastName"
              validators={{
                onBlur: ({ value }) => Validators.required.string(value)
              }}
              children={(contactLastNameField) => (
                <FF
                  label="Last Name"
                  field={contactLastNameField}
                  required
                  input={(inputProps) => <TextInput data-qa="Last Name" {...inputProps} />}
                />
              )}
            />
          </Col>
        </FormRow>

        <FormRow>
          <Col>
            <form.Field
              name="email"
              validators={{
                onBlur: ({ value }) => Validators.email.requiredAndValid(value)
              }}
              children={(emailField) => (
                <FF
                  label="Email"
                  field={emailField}
                  required
                  input={(inputProps) => <EmailInput data-qa="Email" {...inputProps} />}
                />
              )}
            />
          </Col>
          <Col>
            <form.Field
              name="phone"
              validators={{
                onBlur: ({ value }) => Validators.required.notEmpty(value)
              }}
              children={(phoneField) => (
                <FF
                  label="Phone"
                  field={phoneField}
                  required
                  input={(inputProps) => <USPhoneInput data-qa="Phone" {...inputProps} />}
                />
              )}
            />
          </Col>
        </FormRow>

        <FormRow>
          <Col2x>
            <form.Field
              name="bankNotes"
              children={(bankNotesField) => (
                <FF
                  label="Notes for the Applicant (visible for client)"
                  field={bankNotesField}
                  input={(inputProps) => <TextareaInput data-qa="Notes for the Applicant" {...inputProps} />}
                />
              )}
            />
          </Col2x>
        </FormRow>

        <FormRow>
          <Col2x>
            <form.Field
              name="internalBankNotes"
              children={(internalBankNotesField) => (
                <FF
                  label="Internal Notes (only visible to Bank)"
                  field={internalBankNotesField}
                  input={(inputProps) => <TextareaInput data-qa="Internal Notes" {...inputProps} />}
                />
              )}
            />
          </Col2x>
        </FormRow>
      </DialogContent>
      <DialogFooter hasSubmit hasCancel submitText={'Add'} onSubmit={form.handleSubmit} />
    </>
  )
}
